import { bigDecimalToNumberSafe, sqrt } from './bigDecimal';
import { BigDecimal, Option } from 'effect';

/**
 * Quadratic scale utilities for range slider
 *
 * The slider uses a quadratic (power of 2) scale to provide better granularity
 * for lower values while still allowing selection of higher values.
 *
 * This is milder than logarithmic scaling and provides better UX:
 * - The value median is closer to 0 than to the maximum
 * - Users need fine-grained control in lower value ranges
 * - Larger steps are acceptable for higher value ranges
 * - First steps (0, 100, 200) don't jump too aggressively
 *
 * Works with BigDecimal for precise calculations across all range types
 * (price, RAM, SSD, warranty, battery life, etc.)
 */

/**
 * Converts a linear slider position (0-100) to an actual value
 * using quadratic transformation
 *
 * Uses BigDecimal arithmetic throughout for precision-preserving calculations
 *
 * @param sliderValue - Linear position on slider (0-100)
 * @param min - Minimum value in the range
 * @param max - Maximum value in the range
 * @returns Actual value as BigDecimal
 *
 * @example
 * // For range 0-10000
 * sliderToPrice(50, min, max) // Returns ~2500 (50% squared of max)
 * sliderToPrice(70, min, max) // Returns ~4900 (70% squared)
 */
export const sliderToPrice = (
  sliderValue: number,
  min: BigDecimal.BigDecimal,
  max: BigDecimal.BigDecimal
): BigDecimal.BigDecimal => {
  // Convert slider value to BigDecimal (safe conversion with fallback to min)
  const sliderValueOption = BigDecimal.fromNumber(sliderValue);
  const sliderValueBD = Option.getOrElse(sliderValueOption, () => min);
  const hundred = BigDecimal.fromNumberUnsafe(100);

  // Normalize slider value to 0-1 range using BigDecimal division
  const positionOption = BigDecimal.divide(sliderValueBD, hundred);

  // Handle division (should always succeed with non-zero divisor)
  if (Option.isNone(positionOption)) {
    return min; // Fallback to min if division fails
  }

  const position = positionOption.value;

  // Apply quadratic transformation (square the position) using BigDecimal
  const transformedPosition = BigDecimal.multiply(position, position);

  // Calculate value range using BigDecimal arithmetic for precision
  const range = BigDecimal.subtract(max, min);

  // Map to value range: min + (transformedPosition * range)
  const scaledRange = BigDecimal.multiply(range, transformedPosition);
  const result = BigDecimal.sum(min, scaledRange);

  return result;
};

/**
 * Converts an actual value to a linear slider position (0-100)
 * using inverse quadratic transformation
 *
 * Uses BigDecimal arithmetic for precise calculations, only converts to number for final display
 *
 * @param value - Actual value
 * @param min - Minimum value in the range
 * @param max - Maximum value in the range
 * @returns Linear slider position (0-100)
 *
 * @example
 * // For range 0-10000
 * priceToSlider(value2500, min, max) // Returns ~50
 * priceToSlider(value4900, min, max) // Returns ~70
 */
export const priceToSlider = (
  value: BigDecimal.BigDecimal,
  min: BigDecimal.BigDecimal,
  max: BigDecimal.BigDecimal
): number => {
  // Calculate range using BigDecimal arithmetic
  const range = BigDecimal.subtract(max, min);

  // Handle edge case: if range is zero, return 0
  if (BigDecimal.isZero(range)) {
    return 0;
  }

  // Normalize value to 0-1 range: (value - min) / (max - min)
  const valueDiff = BigDecimal.subtract(value, min);
  const normalizedValueOption = BigDecimal.divide(valueDiff, range);

  // Handle division by zero (should not happen due to check above, but be safe)
  if (Option.isNone(normalizedValueOption)) {
    return 0;
  }

  const normalizedValue = normalizedValueOption.value;

  // Ensure non-negative for sqrt (clamp to zero minimum)
  const nonNegativeValue = BigDecimal.isGreaterThanOrEqualTo(
    normalizedValue,
    BigDecimal.fromNumberUnsafe(0)
  )
    ? normalizedValue
    : BigDecimal.fromNumberUnsafe(0);

  // Apply inverse quadratic transformation (square root) using BigDecimal wrapper
  const positionOption = sqrt(nonNegativeValue);

  // Convert to 0-100 range (fallback to 0 if sqrt fails)
  const position = Option.getOrElse(() => 0)(
    Option.flatMap(positionOption, (pos) => bigDecimalToNumberSafe(pos))
  );

  return position * 100;
};
